import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Users, UserPlus, Loader2, Search, Pencil, Building2, Briefcase, X,
  Cake, CalendarClock, TrendingUp, IdCard, FileWarning, Wallet, Plus, Trash2,
} from 'lucide-react'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { buscarCep } from '@/lib/viacep'
import { enterAbrePrimeiro } from '@/lib/keyboard'
import { pedirExclusao } from '@/lib/confirmarExclusao'

const UFS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO']

const STATUS_META: Record<string, { label: string; cor: string }> = {
  ativo:     { label: 'Ativo',     cor: 'bg-emerald-100 text-emerald-700' },
  ferias:    { label: 'Férias',    cor: 'bg-sky-100 text-sky-700' },
  afastado:  { label: 'Afastado',  cor: 'bg-amber-100 text-amber-700' },
  demitido:  { label: 'Demitido',  cor: 'bg-rose-100 text-rose-700' },
}
const CONTRATO = ['clt', 'pj', 'estagio', 'temporario', 'autonomo']
const CORES = ['#0ea5e9', '#22c55e', '#f59e0b', '#a855f7', '#ef4444', '#14b8a6', '#6366f1']

function iniciais(nome: string) {
  const p = (nome || '').trim().split(/\s+/)
  return ((p[0]?.[0] || '') + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase()
}
function Avatar({ nome, url, size = 40 }: { nome: string; url?: string | null; size?: number }) {
  if (url) return <img src={url} alt={nome} className="rounded-full object-cover" style={{ width: size, height: size }} />
  return (
    <div className="rounded-full bg-brand-100 text-brand-700 font-semibold flex items-center justify-center shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.38 }}>{iniciais(nome)}</div>
  )
}

// ==================================================== DASHBOARD
function DashboardRH() {
  const { data, isLoading } = useQuery<any>({ queryKey: ['rh-dashboard'], queryFn: async () => (await api.get('/rh/dashboard')).data })
  if (isLoading || !data) return <div className="p-10 text-center text-ink-400"><Loader2 className="animate-spin inline" /> Carregando…</div>
  const pieStatus = Object.entries(data.por_status || {}).map(([k, v]) => ({ name: STATUS_META[k]?.label || k, value: v as number }))
  const Card = ({ icon: Icon, label, valor, cor, sub }: any) => (
    <div className="card card-body flex items-center gap-3">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${cor}`}><Icon size={22} /></div>
      <div><div className="text-2xl font-bold leading-none">{valor}</div>
        <div className="text-xs text-ink-500 mt-1">{label}</div>{sub && <div className="text-[11px] text-ink-400">{sub}</div>}</div>
    </div>
  )
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card icon={Users} label="Colaboradores ativos" valor={data.headcount} cor="bg-brand-100 text-brand-700" />
        <Card icon={Wallet} label="Folha mensal (salários)" valor={formatMoney(data.folha_mensal)} cor="bg-emerald-100 text-emerald-700" />
        <Card icon={CalendarClock} label="Em férias / afastados" valor={(data.por_status?.ferias || 0) + (data.por_status?.afastado || 0)} cor="bg-amber-100 text-amber-700" />
        <Card icon={TrendingUp} label="Admissões (30d)" valor={(data.admissoes_recentes || []).length} cor="bg-sky-100 text-sky-700" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Distribuição */}
        <div className="card card-body">
          <div className="font-semibold text-sm mb-2 flex items-center gap-2"><Building2 size={15} /> Por departamento</div>
          <div className="space-y-1.5">
            {(data.por_departamento || []).map((d: any, i: number) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: CORES[i % CORES.length] }} />
                <span className="flex-1 truncate">{d.nome}</span><span className="font-semibold">{d.qtd}</span>
              </div>
            ))}
            {!(data.por_departamento || []).length && <div className="text-xs text-ink-400">Sem departamentos.</div>}
          </div>
        </div>
        {/* Status pie */}
        <div className="card card-body">
          <div className="font-semibold text-sm mb-2">Situação do quadro</div>
          <ResponsiveContainer width="100%" height={150}>
            <PieChart><Pie data={pieStatus} dataKey="value" nameKey="name" innerRadius={38} outerRadius={62} paddingAngle={2}>
              {pieStatus.map((_, i) => <Cell key={i} fill={CORES[i % CORES.length]} />)}</Pie><Tooltip /></PieChart>
          </ResponsiveContainer>
        </div>
        {/* Aniversariantes */}
        <div className="card card-body">
          <div className="font-semibold text-sm mb-2 flex items-center gap-2"><Cake size={15} className="text-pink-500" /> Aniversariantes do mês</div>
          <div className="space-y-2 max-h-44 overflow-auto">
            {(data.aniversariantes_mes || []).map((a: any) => (
              <div key={a.id} className="flex items-center gap-2 text-sm"><Avatar nome={a.nome} url={a.foto_url} size={28} />
                <span className="flex-1 truncate">{a.nome}</span><span className="text-xs text-pink-600 font-semibold">dia {a.dia}</span></div>
            ))}
            {!(data.aniversariantes_mes || []).length && <div className="text-xs text-ink-400">Ninguém faz aniversário este mês.</div>}
          </div>
        </div>
      </div>
      {/* Documentos vencendo */}
      {(data.documentos_vencendo || []).length > 0 && (
        <div className="card card-body border-l-4 border-amber-400">
          <div className="font-semibold text-sm mb-2 flex items-center gap-2 text-amber-700"><FileWarning size={15} /> Documentos vencendo / vencidos</div>
          <div className="grid md:grid-cols-2 gap-1.5">
            {data.documentos_vencendo.map((d: any, i: number) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${d.dias < 0 ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>
                  {d.dias < 0 ? `vencido ${-d.dias}d` : `${d.dias}d`}</span>
                <span className="flex-1 truncate">{d.colaborador} — {d.tipo}</span>
                <span className="text-xs text-ink-400">{d.validade.split('-').reverse().join('/')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ==================================================== COLABORADORES
function Colaboradores() {
  const qc = useQueryClient()
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const [editing, setEditing] = useState<any>(null)
  const [novo, setNovo] = useState(false)
  const { data: lista = [], isLoading } = useQuery<any[]>({
    queryKey: ['rh-colaboradores', status],
    queryFn: async () => (await api.get('/rh/colaboradores', { params: { status: status || undefined } })).data,
  })
  const filtrados = lista.filter(c => !q || (c.nome + (c.cpf || '') + (c.matricula || '')).toLowerCase().includes(q.toLowerCase()))
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <input className="input pl-9 !py-1.5 text-sm" placeholder="Buscar por nome, CPF, matrícula…" value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={enterAbrePrimeiro(filtrados, c => { setEditing(c); setNovo(true) })} />
        </div>
        {['', 'ativo', 'ferias', 'afastado', 'demitido'].map(s => (
          <button key={s || 'todos'} onClick={() => setStatus(s)}
            className={`px-3 py-1 rounded text-xs ${status === s ? 'bg-brand-600 text-white' : 'bg-ink-100 text-ink-700'}`}>
            {s === '' ? 'Todos' : STATUS_META[s].label}</button>
        ))}
        <button onClick={() => { setEditing(null); setNovo(true) }} className="btn-primary text-sm flex items-center gap-1"><UserPlus size={15} /> Novo colaborador</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {isLoading && <div className="col-span-full p-8 text-center text-ink-400"><Loader2 className="animate-spin inline" /></div>}
        {!isLoading && filtrados.length === 0 && <div className="col-span-full p-8 text-center text-ink-400">Nenhum colaborador.</div>}
        {filtrados.map(c => (
          <button key={c.id} onClick={() => { setEditing(c); setNovo(true) }}
            className="card card-body text-left flex items-center gap-3 hover:ring-2 hover:ring-brand-200 transition">
            <Avatar nome={c.nome} url={c.foto_url} size={46} />
            <div className="min-w-0 flex-1">
              <div className="font-semibold truncate">{c.nome}</div>
              <div className="text-xs text-ink-500 truncate">{c.cargo_nome || '—'}{c.departamento_nome ? ` · ${c.departamento_nome}` : ''}</div>
              <div className="mt-1 flex items-center gap-1.5">
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${STATUS_META[c.status]?.cor || 'bg-ink-100'}`}>{STATUS_META[c.status]?.label || c.status}</span>
                <span className="text-[10px] text-ink-400 uppercase">{c.tipo_contrato}</span>
              </div>
            </div>
            <Pencil size={15} className="text-ink-300" />
          </button>
        ))}
      </div>
      {novo && <ColaboradorModal colab={editing} onClose={() => { setNovo(false); setEditing(null) }}
        onSaved={() => { qc.invalidateQueries({ queryKey: ['rh-colaboradores'] }); qc.invalidateQueries({ queryKey: ['rh-dashboard'] }) }} />}
    </div>
  )
}

function ColaboradorModal({ colab, onClose, onSaved }: { colab?: any; onClose: () => void; onSaved: () => void }) {
  const qc = useQueryClient()
  const ed = !!colab?.id
  const [aba, setAba] = useState<'pessoal' | 'contrato' | 'bancario' | 'docs' | 'ponto' | 'vale'>('pessoal')
  const [f, setF] = useState<any>({ tipo_contrato: 'clt', status: 'ativo', salario: 0, ...(colab || {}) })
  const set = (k: string, v: any) => setF((s: any) => ({ ...s, [k]: v }))
  const { data: cargos = [] } = useQuery<any[]>({ queryKey: ['rh-cargos'], queryFn: async () => (await api.get('/rh/cargos')).data })
  const { data: deptos = [] } = useQuery<any[]>({ queryKey: ['rh-deptos'], queryFn: async () => (await api.get('/rh/departamentos')).data })
  const { data: jornadas = [] } = useQuery<any[]>({ queryKey: ['rh-jornadas'], queryFn: async () => (await api.get('/rh/jornadas')).data })
  const salvar = useMutation({
    mutationFn: async () => {
      const body = { ...f, salario: Number(f.salario || 0), cargo_id: f.cargo_id || null, departamento_id: f.departamento_id || null, jornada_id: f.jornada_id || null }
      return ed ? (await api.put(`/rh/colaboradores/${colab.id}`, body)).data : (await api.post('/rh/colaboradores', body)).data
    },
    onSuccess: () => { toast.success(ed ? 'Colaborador atualizado' : 'Colaborador cadastrado'); onSaved(); onClose() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })
  const F = ({ label, k, type = 'text', col = 1 }: any) => (
    <div className={col === 2 ? 'col-span-2' : ''}>
      <label className="text-xs text-ink-500">{label}</label>
      <input type={type} className="input !py-1.5 text-sm" value={f[k] ?? ''} onChange={e => set(k, e.target.value)} />
    </div>
  )
  const abas = [['pessoal', 'Pessoal'], ['contrato', 'Contrato'], ['bancario', 'Bancário'], ...(ed ? [['docs', 'Documentos'], ['ponto', 'Ponto'], ['vale', 'Vale']] : [])] as const
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b flex items-center gap-3">
          <Avatar nome={f.nome || '?'} url={f.foto_url} size={44} />
          <div className="flex-1"><h3 className="font-bold">{ed ? f.nome : 'Novo colaborador'}</h3>
            <div className="text-xs text-ink-400">{ed ? (f.cargo_nome || 'Setor Pessoal') : 'Setor Pessoal'}</div></div>
          <button onClick={onClose}><X size={18} className="text-ink-400" /></button>
        </div>
        <div className="flex gap-1 px-4 border-b">
          {abas.map(([k, l]) => (
            <button key={k} onClick={() => setAba(k as any)}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${aba === k ? 'border-brand-600 text-brand-600' : 'border-transparent text-ink-500'}`}>{l}</button>
          ))}
        </div>
        <div className="p-4 overflow-auto flex-1">
          {aba === 'pessoal' && (
            <div className="grid grid-cols-2 gap-3">
              {F({ label: 'Nome *', k: 'nome', col: 2 })}
              {F({ label: 'CPF', k: 'cpf' })}{F({ label: 'RG', k: 'rg' })}
              {F({ label: 'Nascimento', k: 'data_nascimento', type: 'date' })}
              <div><label className="text-xs text-ink-500">Sexo</label>
                <select className="input !py-1.5 text-sm" value={f.sexo || ''} onChange={e => set('sexo', e.target.value)}>
                  <option value="">—</option><option value="M">Masculino</option><option value="F">Feminino</option><option value="O">Outro</option></select></div>
              {F({ label: 'E-mail', k: 'email' })}{F({ label: 'Telefone', k: 'telefone' })}
              <div>
                <label className="text-xs text-ink-500">CEP</label>
                <input className="input !py-1.5 text-sm" value={f.cep ?? ''} inputMode="numeric" placeholder="00000-000"
                  onChange={e => set('cep', e.target.value)}
                  onBlur={async e => {
                    const r = await buscarCep(e.target.value)
                    if (r) setF((s: any) => ({
                      ...s,
                      endereco: r.logradouro || s.endereco,
                      bairro: r.bairro || s.bairro,
                      cidade: r.localidade || s.cidade,
                      uf: r.uf || s.uf,
                    }))
                  }} />
                <a href="https://buscacepinter.correios.com.br/app/endereco/index.php"
                  target="_blank" rel="noopener noreferrer"
                  className="text-[11px] text-blue-600 hover:underline">Não sei meu CEP</a>
              </div>
              {F({ label: 'Cidade', k: 'cidade' })}
              {F({ label: 'Endereço', k: 'endereco', col: 2 })}
              {F({ label: 'Bairro', k: 'bairro' })}
              <div>
                <label className="text-xs text-ink-500">UF</label>
                <select className="input !py-1.5 text-sm" value={f.uf || ''} onChange={e => set('uf', e.target.value)}>
                  <option value="">—</option>
                  {UFS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              {F({ label: 'Foto (URL)', k: 'foto_url', col: 2 })}
            </div>
          )}
          {aba === 'contrato' && (
            <div className="grid grid-cols-2 gap-3">
              {F({ label: 'Matrícula', k: 'matricula' })}
              <div><label className="text-xs text-ink-500">Tipo de contrato</label>
                <select className="input !py-1.5 text-sm" value={f.tipo_contrato} onChange={e => set('tipo_contrato', e.target.value)}>
                  {CONTRATO.map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}</select></div>
              <div><label className="text-xs text-ink-500">Cargo</label>
                <select className="input !py-1.5 text-sm" value={f.cargo_id || ''} onChange={e => set('cargo_id', e.target.value ? Number(e.target.value) : null)}>
                  <option value="">—</option>{cargos.map((c: any) => <option key={c.id} value={c.id}>{c.nome}</option>)}</select>
                {cargos.length === 0 && <p className="text-[11px] text-amber-600 mt-0.5">Nenhum cargo — cadastre na aba "Cargos &amp; Departamentos".</p>}</div>
              <div><label className="text-xs text-ink-500">Departamento</label>
                <select className="input !py-1.5 text-sm" value={f.departamento_id || ''} onChange={e => set('departamento_id', e.target.value ? Number(e.target.value) : null)}>
                  <option value="">—</option>{deptos.map((d: any) => <option key={d.id} value={d.id}>{d.nome}</option>)}</select>
                {deptos.length === 0 && <p className="text-[11px] text-amber-600 mt-0.5">Nenhum departamento — cadastre na aba "Cargos &amp; Departamentos".</p>}</div>
              {F({ label: 'Admissão', k: 'data_admissao', type: 'date' })}{F({ label: 'Demissão', k: 'data_demissao', type: 'date' })}
              {F({ label: 'Salário (R$)', k: 'salario', type: 'number' })}
              <div><label className="text-xs text-ink-500">Situação</label>
                <select className="input !py-1.5 text-sm" value={f.status} onChange={e => set('status', e.target.value)}>
                  {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></div>
              <div><label className="text-xs text-ink-500">Jornada (ponto)</label>
                <select className="input !py-1.5 text-sm" value={f.jornada_id || ''} onChange={e => set('jornada_id', e.target.value ? Number(e.target.value) : null)}>
                  <option value="">— sem jornada —</option>{jornadas.map((j: any) => <option key={j.id} value={j.id}>{j.nome}</option>)}</select></div>
              {F({ label: 'Jornada (texto livre)', k: 'jornada_desc' })}
              {F({ label: 'PIS', k: 'pis' })}{F({ label: 'CTPS nº', k: 'ctps_numero' })}
            </div>
          )}
          {aba === 'bancario' && (
            <div className="grid grid-cols-2 gap-3">
              {F({ label: 'Banco', k: 'banco' })}{F({ label: 'Agência', k: 'banco_agencia' })}
              {F({ label: 'Conta', k: 'banco_conta' })}{F({ label: 'Chave PIX', k: 'pix' })}
              {F({ label: 'Observação', k: 'observacao', col: 2 })}
            </div>
          )}
          {aba === 'docs' && ed && <DocsColaborador colaboradorId={colab.id} />}
          {aba === 'ponto' && ed && <PontoColaborador colaboradorId={colab.id} />}
          {aba === 'vale' && ed && <AdiantamentosColaborador colaboradorId={colab.id} />}
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm rounded border">Cancelar</button>
          <button onClick={() => salvar.mutate()} disabled={!f.nome || salvar.isPending}
            className="px-4 py-2 text-sm rounded bg-brand-600 text-white disabled:opacity-50">{salvar.isPending ? '…' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  )
}

function DocsColaborador({ colaboradorId }: { colaboradorId: number }) {
  const qc = useQueryClient()
  const [novo, setNovo] = useState<any>({ tipo: 'ASO', descricao: '', arquivo_url: '', validade: '' })
  const { data: docs = [] } = useQuery<any[]>({ queryKey: ['rh-docs', colaboradorId], queryFn: async () => (await api.get(`/rh/colaboradores/${colaboradorId}/documentos`)).data })
  const add = useMutation({
    mutationFn: async () => api.post(`/rh/colaboradores/${colaboradorId}/documentos`, { ...novo, validade: novo.validade || null }),
    onSuccess: () => { toast.success('Documento adicionado'); setNovo({ tipo: 'ASO', descricao: '', arquivo_url: '', validade: '' }); qc.invalidateQueries({ queryKey: ['rh-docs', colaboradorId] }) },
  })
  const del = useMutation({ mutationFn: async (id: number) => api.delete(`/rh/documentos/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ['rh-docs', colaboradorId] }) })
  return (
    <div className="space-y-3">
      {docs.map((d: any) => (
        <div key={d.id} className="flex items-center gap-2 text-sm border-b pb-1">
          <IdCard size={15} className="text-ink-400" />
          <span className="flex-1">{d.tipo}{d.descricao ? ` — ${d.descricao}` : ''}{d.validade ? ` (val. ${d.validade.split('-').reverse().join('/')})` : ''}</span>
          {d.arquivo_url && <a href={d.arquivo_url} target="_blank" rel="noreferrer" className="text-brand-600 text-xs">abrir</a>}
          <button onClick={() => pedirExclusao({ descricao: `o documento "${d.tipo}"`, onConfirm: () => del.mutate(d.id) })} className="text-rose-500"><Trash2 size={14} /></button>
        </div>
      ))}
      {!docs.length && <div className="text-xs text-ink-400">Nenhum documento.</div>}
      <div className="grid grid-cols-4 gap-2 items-end pt-2">
        <div><label className="text-xs text-ink-500">Tipo</label>
          <select className="input !py-1 text-sm" value={novo.tipo} onChange={e => setNovo({ ...novo, tipo: e.target.value })}>
            {['ASO', 'contrato', 'CTPS', 'RG', 'CPF', 'comprovante', 'outro'].map(t => <option key={t}>{t}</option>)}</select></div>
        <div className="col-span-1"><label className="text-xs text-ink-500">Validade</label>
          <input type="date" className="input !py-1 text-sm" value={novo.validade} onChange={e => setNovo({ ...novo, validade: e.target.value })} /></div>
        <div><label className="text-xs text-ink-500">URL</label>
          <input className="input !py-1 text-sm" value={novo.arquivo_url} onChange={e => setNovo({ ...novo, arquivo_url: e.target.value })} /></div>
        <button onClick={() => add.mutate()} className="btn-primary !py-1.5 text-sm">Add</button>
      </div>
    </div>
  )
}

function PontoColaborador({ colaboradorId }: { colaboradorId: number }) {
  const qc = useQueryClient()
  const [mes, setMes] = useState(() => new Date().toISOString().slice(0, 7))
  const [pin, setPin] = useState('')
  const { data, isLoading } = useQuery<any>({
    queryKey: ['rh-ponto', colaboradorId, mes],
    queryFn: async () => (await api.get(`/rh/colaboradores/${colaboradorId}/ponto`, { params: { mes } })).data,
  })
  const salvarPin = useMutation({
    mutationFn: async () => api.post(`/rh/colaboradores/${colaboradorId}/pin`, { pin }),
    onSuccess: () => { toast.success('PIN definido — colaborador já pode usar o app'); setPin(''); qc.invalidateQueries({ queryKey: ['rh-ponto', colaboradorId] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  return (
    <div className="space-y-3">
      <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-900 flex items-center gap-3">
        <div className="flex-1">
          <strong>App de ponto:</strong> o colaborador acessa em <code className="bg-white px-1 rounded">/m/ponto</code> com <strong>CPF + PIN</strong>.
          {data && <span className="ml-1">{data.tem_pin ? '✅ PIN já definido.' : '⚠️ Sem PIN ainda.'}</span>}
        </div>
      </div>
      <div className="flex items-end gap-2">
        <div><label className="text-xs text-ink-500">{data?.tem_pin ? 'Resetar' : 'Definir'} PIN (4-6 dígitos)</label>
          <input className="input !py-1.5 text-sm w-40" inputMode="numeric" maxLength={6} value={pin}
            onChange={e => setPin(e.target.value.replace(/\D/g, ''))} placeholder="••••" /></div>
        <button onClick={() => salvarPin.mutate()} disabled={pin.length < 4 || salvarPin.isPending}
          className="btn-primary !py-1.5 text-sm disabled:opacity-50">Salvar PIN</button>
        <div className="flex-1" />
        <input type="month" className="input !py-1.5 text-sm w-40" value={mes} onChange={e => setMes(e.target.value)} />
      </div>
      {/* Resumo do mês */}
      {data && (
        <div className="grid grid-cols-3 gap-2">
          <div className="card card-body !py-2 text-center"><div className="text-[11px] text-ink-500">Banco de horas</div>
            <div className={`text-lg font-bold ${(data.banco_min || 0) < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{data.banco_horas}</div></div>
          <div className="card card-body !py-2 text-center"><div className="text-[11px] text-ink-500">Atrasos</div><div className="text-lg font-bold text-amber-600">{data.total_atraso}</div></div>
          <div className="card card-body !py-2 text-center"><div className="text-[11px] text-ink-500">Extras</div><div className="text-lg font-bold text-sky-600">{data.total_extra}</div></div>
        </div>
      )}
      {data && !data.tem_jornada && <div className="text-[11px] text-amber-700 bg-amber-50 rounded px-2 py-1">⚠️ Sem jornada definida — sem cálculo de atraso/saldo. Defina a jornada na aba Contrato.</div>}
      <div className="border rounded-lg divide-y max-h-72 overflow-auto">
        {isLoading && <div className="p-4 text-center text-ink-400"><Loader2 className="animate-spin inline" size={16} /></div>}
        {!isLoading && !(data?.dias || []).length && <div className="p-4 text-center text-ink-400 text-sm">Sem marcações neste mês.</div>}
        {(data?.dias || []).map((d: any) => (
          <div key={d.data} className="p-2.5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-ink-500">{d.data.split('-').reverse().join('/')}</span>
              <span className="text-[11px] flex items-center gap-2">
                {d.previsto !== '—' && <span className="text-ink-400">prev {d.previsto} · trab {d.trabalhado}</span>}
                {d.atraso && <span className="text-amber-600">atraso {d.atraso}</span>}
                {d.extra && <span className="text-sky-600">extra {d.extra}</span>}
                {d.previsto !== '—' && <span className={`font-semibold ${d.saldo_min < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>saldo {d.saldo}</span>}
                {d.falta && <span className="text-rose-600 font-semibold">FALTA</span>}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {d.marcacoes.map((m: any) => (
                <span key={m.id} className={`text-xs rounded px-2 py-1 flex items-center gap-1 ${m.fora_cerca ? 'bg-amber-100 text-amber-800' : 'bg-ink-100'}`} title={m.origem + (m.fora_cerca ? ' · fora da cerca' : '')}>
                  <b className="font-mono">{m.hora}</b> {m.label}
                  {m.foto_url && <a href={m.foto_url} target="_blank" rel="noreferrer" className="text-brand-600">📷</a>}
                  {m.lat != null && <a href={`https://maps.google.com/?q=${m.lat},${m.lon}`} target="_blank" rel="noreferrer" className="text-brand-600">📍</a>}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function AdiantamentosColaborador({ colaboradorId }: { colaboradorId: number }) {
  const qc = useQueryClient()
  const [valor, setValor] = useState('')
  const [bancoId, setBancoId] = useState<number | ''>('')
  const [data, setData] = useState(() => new Date().toISOString().slice(0, 10))
  const { data: lista } = useQuery<any>({ queryKey: ['rh-vales', colaboradorId], queryFn: async () => (await api.get(`/rh/colaboradores/${colaboradorId}/adiantamentos`)).data })
  const { data: bancos = [] } = useQuery<any[]>({ queryKey: ['contas-banco-rh'], queryFn: async () => (await api.get('/financeiro/contas-bancarias')).data })
  const refresh = () => { qc.invalidateQueries({ queryKey: ['rh-vales', colaboradorId] }); qc.invalidateQueries({ queryKey: ['folha'] }) }
  const lancar = useMutation({
    mutationFn: async () => api.post('/rh/adiantamentos', { colaborador_id: colaboradorId, valor: Number(valor), conta_bancaria_id: bancoId || null, data }),
    onSuccess: (r: any) => { toast.success(r.data.movimentou_caixa ? 'Vale lançado — saiu do banco' : 'Vale lançado'); setValor(''); refresh() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const excluir = useMutation({ mutationFn: async (id: number) => api.delete(`/rh/adiantamentos/${id}`),
    onSuccess: () => { toast.success('Vale estornado — dinheiro devolvido ao banco'); refresh() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro') })
  return (
    <div className="space-y-3">
      <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-900 flex items-center justify-between">
        <span>💸 <strong>Vale/Adiantamento</strong>: sai do banco na hora e é <strong>descontado automático</strong> na próxima folha.</span>
        <span className="text-right">Em aberto<br/><b className="text-base">{formatMoney(lista?.total_aberto || 0)}</b></span>
      </div>
      <div className="grid grid-cols-4 gap-2 items-end">
        <div><label className="text-xs text-ink-500">Valor (R$)</label><input type="number" className="input !py-1.5 text-sm" value={valor} onChange={e => setValor(e.target.value)} /></div>
        <div><label className="text-xs text-ink-500">Data</label><input type="date" className="input !py-1.5 text-sm" value={data} onChange={e => setData(e.target.value)} /></div>
        <div><label className="text-xs text-ink-500">Sai do banco</label>
          <select className="input !py-1.5 text-sm" value={bancoId} onChange={e => setBancoId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">— não movimenta —</option>{bancos.map((b: any) => <option key={b.id} value={b.id}>{b.nome}</option>)}</select></div>
        <button onClick={() => lancar.mutate()} disabled={!valor || lancar.isPending} className="btn-primary !py-1.5 text-sm disabled:opacity-50">Lançar vale</button>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-ink-500 border-b text-xs"><th className="py-1">Data</th><th className="text-right">Valor</th><th>Banco</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {(lista?.itens || []).map((a: any) => (
            <tr key={a.id} className="border-b">
              <td className="py-1.5">{a.data?.split('-').reverse().join('/')}</td>
              <td className="text-right font-medium">{formatMoney(a.valor)}</td>
              <td className="text-xs text-ink-500">{a.banco_nome || '—'}</td>
              <td><span className={`text-[10px] px-1.5 py-0.5 rounded ${a.status === 'descontado' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{a.status === 'descontado' ? 'descontado' : 'em aberto'}</span></td>
              <td>{a.status !== 'descontado' && <button onClick={() => pedirExclusao({ descricao: 'o adiantamento', onConfirm: () => excluir.mutate(a.id) })} className="text-rose-500"><Trash2 size={13} /></button>}</td>
            </tr>
          ))}
          {!(lista?.itens || []).length && <tr><td colSpan={5} className="py-3 text-center text-ink-400 text-xs">Nenhum vale.</td></tr>}
        </tbody>
      </table>
    </div>
  )
}

// ==================================================== CARGOS & DEPARTAMENTOS
function CargosDeptos() {
  const qc = useQueryClient()
  const { data: cargos = [] } = useQuery<any[]>({ queryKey: ['rh-cargos'], queryFn: async () => (await api.get('/rh/cargos')).data })
  const { data: deptos = [] } = useQuery<any[]>({ queryKey: ['rh-deptos'], queryFn: async () => (await api.get('/rh/departamentos')).data })
  const [novoCargo, setNovoCargo] = useState({ nome: '', cbo: '', salario_base: '' })
  const [novoDepto, setNovoDepto] = useState('')
  const refresh = () => { qc.invalidateQueries({ queryKey: ['rh-cargos'] }); qc.invalidateQueries({ queryKey: ['rh-deptos'] }) }
  const addCargo = useMutation({ mutationFn: async () => api.post('/rh/cargos', { nome: novoCargo.nome, cbo: novoCargo.cbo || null, salario_base: Number(novoCargo.salario_base || 0) }), onSuccess: () => { toast.success('Cargo criado'); setNovoCargo({ nome: '', cbo: '', salario_base: '' }); refresh() } })
  const delCargo = useMutation({ mutationFn: async (id: number) => api.delete(`/rh/cargos/${id}`), onSuccess: refresh })
  const addDepto = useMutation({ mutationFn: async () => api.post('/rh/departamentos', { nome: novoDepto }), onSuccess: () => { toast.success('Departamento criado'); setNovoDepto(''); refresh() } })
  const delDepto = useMutation({ mutationFn: async (id: number) => api.delete(`/rh/departamentos/${id}`), onSuccess: refresh })
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="card card-body space-y-2">
        <div className="font-semibold flex items-center gap-2"><Briefcase size={16} /> Cargos</div>
        {cargos.map((c: any) => (
          <div key={c.id} className="flex items-center gap-2 text-sm border-b pb-1">
            <span className="flex-1">{c.nome} {c.cbo && <span className="text-ink-400">· CBO {c.cbo}</span>}</span>
            <span className="text-ink-500">{formatMoney(c.salario_base)}</span>
            <span className="text-[10px] text-ink-400">{c.qtd_colaboradores}👤</span>
            <button onClick={() => pedirExclusao({ descricao: `o cargo "${c.nome}"`, onConfirm: () => delCargo.mutate(c.id) })} className="text-rose-500"><Trash2 size={13} /></button>
          </div>
        ))}
        <div className="grid grid-cols-4 gap-1.5 items-end pt-1">
          <input className="input !py-1 text-sm col-span-2" placeholder="Cargo" value={novoCargo.nome} onChange={e => setNovoCargo({ ...novoCargo, nome: e.target.value })} />
          <input className="input !py-1 text-sm" placeholder="R$ base" type="number" value={novoCargo.salario_base} onChange={e => setNovoCargo({ ...novoCargo, salario_base: e.target.value })} />
          <button onClick={() => addCargo.mutate()} disabled={!novoCargo.nome} className="btn-primary !py-1.5 text-sm disabled:opacity-50"><Plus size={14} /></button>
        </div>
      </div>
      <div className="card card-body space-y-2">
        <div className="font-semibold flex items-center gap-2"><Building2 size={16} /> Departamentos</div>
        {deptos.map((d: any) => (
          <div key={d.id} className="flex items-center gap-2 text-sm border-b pb-1">
            <span className="flex-1">{d.nome}</span><span className="text-[10px] text-ink-400">{d.qtd_colaboradores}👤</span>
            <button onClick={() => pedirExclusao({ descricao: `o departamento "${d.nome}"`, onConfirm: () => delDepto.mutate(d.id) })} className="text-rose-500"><Trash2 size={13} /></button>
          </div>
        ))}
        <div className="flex gap-1.5 pt-1">
          <input className="input !py-1 text-sm flex-1" placeholder="Novo departamento" value={novoDepto} onChange={e => setNovoDepto(e.target.value)} />
          <button onClick={() => addDepto.mutate()} disabled={!novoDepto} className="btn-primary !py-1.5 text-sm disabled:opacity-50"><Plus size={14} /></button>
        </div>
      </div>
    </div>
  )
}

// ==================================================== JORNADAS & PONTO
const DIAS_SEMANA = [['0', 'Seg'], ['1', 'Ter'], ['2', 'Qua'], ['3', 'Qui'], ['4', 'Sex'], ['5', 'Sáb'], ['6', 'Dom']] as const

function JornadaModal({ jornada, onClose, onSaved }: { jornada?: any; onClose: () => void; onSaved: () => void }) {
  const ed = !!jornada?.id
  const [nome, setNome] = useState(jornada?.nome || '')
  const [tol, setTol] = useState(jornada?.tolerancia_min ?? 10)
  const [dias, setDias] = useState<any>(jornada?.dias || {})
  const setDia = (k: string, campo: string, v: string) => setDias((s: any) => ({ ...s, [k]: { ...(s[k] || {}), [campo]: v } }))
  const toggleFolga = (k: string) => setDias((s: any) => { const n = { ...s }; if (n[k]) delete n[k]; else n[k] = { ent: '08:00', sai: '18:00', int: 60 }; return n })
  const salvar = useMutation({
    mutationFn: async () => {
      const clean: any = {}
      for (const [k, v] of Object.entries(dias)) { const d: any = v; if (d?.ent && d?.sai) clean[k] = { ent: d.ent, sai: d.sai, int: Number(d.int || 0) } }
      const body = { nome, dias: clean, tolerancia_min: Number(tol || 0), ativo: true }
      return ed ? api.put(`/rh/jornadas/${jornada.id}`, body) : api.post('/rh/jornadas', body)
    },
    onSuccess: () => { toast.success(ed ? 'Jornada atualizada' : 'Jornada criada'); onSaved(); onClose() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h3 className="font-bold">{ed ? 'Editar jornada' : 'Nova jornada'}</h3><button onClick={onClose}><X size={18} className="text-ink-400" /></button></div>
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2"><label className="text-xs text-ink-500">Nome</label><input className="input !py-1.5 text-sm" value={nome} onChange={e => setNome(e.target.value)} placeholder="Comercial 08-18" /></div>
          <div><label className="text-xs text-ink-500">Tolerância (min)</label><input type="number" className="input !py-1.5 text-sm" value={tol} onChange={e => setTol(Number(e.target.value))} /></div>
        </div>
        <div className="space-y-1">
          {DIAS_SEMANA.map(([k, lbl]) => {
            const d = dias[k]
            return (
              <div key={k} className="flex items-center gap-2 text-sm">
                <button onClick={() => toggleFolga(k)} className={`w-12 text-xs rounded py-1 font-medium ${d ? 'bg-brand-100 text-brand-700' : 'bg-ink-100 text-ink-400'}`}>{lbl}</button>
                {d ? (<>
                  <input type="time" className="input !py-1 text-xs w-24" value={d.ent || ''} onChange={e => setDia(k, 'ent', e.target.value)} />
                  <input type="time" className="input !py-1 text-xs w-24" value={d.sai || ''} onChange={e => setDia(k, 'sai', e.target.value)} />
                  <input type="number" className="input !py-1 text-xs w-16" value={d.int ?? 0} onChange={e => setDia(k, 'int', e.target.value)} title="intervalo (min)" />
                  <span className="text-[10px] text-ink-400">int</span>
                </>) : <span className="text-xs text-ink-400">folga</span>}
              </div>
            )
          })}
        </div>
        <div className="flex justify-end gap-2"><button onClick={onClose} className="px-3 py-2 text-sm rounded border">Cancelar</button>
          <button onClick={() => salvar.mutate()} disabled={!nome} className="px-4 py-2 text-sm rounded bg-brand-600 text-white disabled:opacity-50">Salvar</button></div>
      </div>
    </div>
  )
}

function JornadasPonto() {
  const qc = useQueryClient()
  const [modal, setModal] = useState(false); const [editing, setEditing] = useState<any>(null)
  const { data: jornadas = [] } = useQuery<any[]>({ queryKey: ['rh-jornadas'], queryFn: async () => (await api.get('/rh/jornadas')).data })
  const { data: cfg } = useQuery<any>({ queryKey: ['rh-ponto-config'], queryFn: async () => (await api.get('/rh/ponto-config')).data })
  const [g, setG] = useState<any>(null)
  if (cfg && !g) setG(cfg)
  const refreshJ = () => qc.invalidateQueries({ queryKey: ['rh-jornadas'] })
  const delJ = useMutation({ mutationFn: async (id: number) => api.delete(`/rh/jornadas/${id}`), onSuccess: refreshJ })
  const salvarCfg = useMutation({
    mutationFn: async () => api.put('/rh/ponto-config', { ...g, geofence_lat: g.geofence_lat || null, geofence_lon: g.geofence_lon || null }),
    onSuccess: () => { toast.success('Configuração salva'); qc.invalidateQueries({ queryKey: ['rh-ponto-config'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="card card-body space-y-2">
        <div className="flex items-center justify-between"><div className="font-semibold flex items-center gap-2"><CalendarClock size={16} /> Jornadas</div>
          <button onClick={() => { setEditing(null); setModal(true) }} className="btn-primary !py-1 text-xs flex items-center gap-1"><Plus size={13} /> Nova</button></div>
        {jornadas.map((j: any) => (
          <div key={j.id} className="flex items-center gap-2 text-sm border-b pb-1">
            <span className="flex-1">{j.nome} <span className="text-ink-400 text-xs">· {j.carga_semanal}/sem · tol {j.tolerancia_min}min</span></span>
            <span className="text-[10px] text-ink-400">{j.qtd_colaboradores}👤</span>
            <button onClick={() => { setEditing(j); setModal(true) }} className="text-ink-500 hover:text-brand-600"><Pencil size={13} /></button>
            <button onClick={() => pedirExclusao({ descricao: `a jornada "${j.nome}"`, onConfirm: () => delJ.mutate(j.id) })} className="text-rose-500"><Trash2 size={13} /></button>
          </div>
        ))}
        {!jornadas.length && <div className="text-xs text-ink-400">Nenhuma jornada. Crie uma e atribua aos colaboradores (aba Contrato).</div>}
      </div>
      <div className="card card-body space-y-3">
        <div className="font-semibold flex items-center gap-2">📍 Cerca (geofence) do ponto</div>
        {g && (<>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={g.geofence_ativo} onChange={e => setG({ ...g, geofence_ativo: e.target.checked })} /> Validar localização ao bater o ponto</label>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs text-ink-500">Latitude</label><input className="input !py-1.5 text-sm" value={g.geofence_lat ?? ''} onChange={e => setG({ ...g, geofence_lat: e.target.value })} placeholder="-7.21" /></div>
            <div><label className="text-xs text-ink-500">Longitude</label><input className="input !py-1.5 text-sm" value={g.geofence_lon ?? ''} onChange={e => setG({ ...g, geofence_lon: e.target.value })} placeholder="-39.31" /></div>
            <div><label className="text-xs text-ink-500">Raio (m)</label><input type="number" className="input !py-1.5 text-sm" value={g.geofence_raio_m} onChange={e => setG({ ...g, geofence_raio_m: Number(e.target.value) })} /></div>
            <div className="flex items-end"><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={g.bloquear_fora} onChange={e => setG({ ...g, bloquear_fora: e.target.checked })} /> Bloquear fora da cerca</label></div>
          </div>
          <p className="text-[11px] text-ink-400">Sem bloqueio: registra e só marca "fora da cerca". Dica: use a lat/long da empresa (Google Maps → clicar no local → copiar coordenadas).</p>
          <button onClick={() => salvarCfg.mutate()} className="btn-primary text-sm self-start">Salvar cerca</button>
        </>)}
      </div>
      {modal && <JornadaModal jornada={editing} onClose={() => { setModal(false); setEditing(null) }} onSaved={refreshJ} />}
    </div>
  )
}

// ==================================================== FOLHA GERENCIAL
function FolhaItemModal({ item, onClose, onSaved }: { item: any; onClose: () => void; onSaved: () => void }) {
  const [prov, setProv] = useState<any[]>(item.proventos || [])
  const [desc, setDesc] = useState<any[]>(item.descontos || [])
  const [comissao, setComissao] = useState(item.comissao ?? 0)
  const salvar = useMutation({
    mutationFn: async () => api.put(`/rh/folha/item/${item.id}`, {
      comissao: Number(comissao || 0),
      proventos: prov.filter(p => p.desc && p.valor), descontos: desc.filter(d => d.desc && d.valor),
    }),
    onSuccess: () => { toast.success('Recalculado'); onSaved(); onClose() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const Lista = ({ titulo, arr, setArr, cor }: any) => (
    <div>
      <div className={`text-xs font-semibold mb-1 ${cor}`}>{titulo}</div>
      {arr.map((x: any, i: number) => (
        <div key={i} className="flex gap-1 mb-1">
          <input className="input !py-1 text-sm flex-1" placeholder="descrição" value={x.desc} onChange={e => setArr(arr.map((a: any, j: number) => j === i ? { ...a, desc: e.target.value } : a))} />
          <input type="number" className="input !py-1 text-sm w-24" placeholder="R$" value={x.valor} onChange={e => setArr(arr.map((a: any, j: number) => j === i ? { ...a, valor: e.target.value } : a))} />
          <button onClick={() => setArr(arr.filter((_: any, j: number) => j !== i))} className="text-rose-500"><Trash2 size={14} /></button>
        </div>
      ))}
      <button onClick={() => setArr([...arr, { desc: '', valor: '' }])} className="text-xs text-brand-600 flex items-center gap-1"><Plus size={12} /> adicionar</button>
    </div>
  )
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h3 className="font-bold">{item.colaborador_nome}</h3><button onClick={onClose}><X size={18} className="text-ink-400" /></button></div>
        <div className="text-sm text-ink-500">Base: {formatMoney(item.salario_base)}</div>
        <div><div className="text-xs font-semibold mb-1">Comissão</div><input type="number" className="input !py-1 text-sm w-32" value={comissao} onChange={e => setComissao(e.target.value)} /></div>
        <Lista titulo="Proventos (+)" arr={prov} setArr={setProv} cor="text-emerald-700" />
        <Lista titulo="Descontos (−)" arr={desc} setArr={setDesc} cor="text-rose-700" />
        <div className="flex justify-end gap-2"><button onClick={onClose} className="px-3 py-2 text-sm rounded border">Cancelar</button>
          <button onClick={() => salvar.mutate()} className="px-4 py-2 text-sm rounded bg-brand-600 text-white">Recalcular</button></div>
      </div>
    </div>
  )
}

function ConfigFolhaModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const { data: cfg } = useQuery<any>({ queryKey: ['folha-config'], queryFn: async () => (await api.get('/rh/folha-config')).data })
  const [f, setF] = useState<any>(null)
  if (cfg && !f) setF(cfg)
  const salvar = useMutation({ mutationFn: async () => api.put('/rh/folha-config', f),
    onSuccess: () => { toast.success('Config salva'); qc.invalidateQueries({ queryKey: ['folha-config'] }); onClose() } })
  if (!f) return null
  const num = (k: string, l: string) => <div><label className="text-xs text-ink-500">{l}</label><input type="number" className="input !py-1.5 text-sm" value={f[k]} onChange={e => setF({ ...f, [k]: Number(e.target.value) })} /></div>
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h3 className="font-bold">Encargos da folha (gerencial)</h3><button onClick={onClose}><X size={18} className="text-ink-400" /></button></div>
        <div className="grid grid-cols-3 gap-2">{num('inss_patronal_pct', 'INSS patronal %')}{num('fgts_pct', 'FGTS %')}{num('outros_encargos_pct', 'RAT+terceiros %')}</div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.provisiona_13} onChange={e => setF({ ...f, provisiona_13: e.target.checked })} /> Provisionar 13º (1/12)</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.provisiona_ferias} onChange={e => setF({ ...f, provisiona_ferias: e.target.checked })} /> Provisionar férias + 1/3</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.puxar_comissao} onChange={e => setF({ ...f, puxar_comissao: e.target.checked })} /> Puxar comissão do Comercial automaticamente</label>
        <div className="text-[11px] text-ink-400">Regime Normal usa ~20% INSS patronal. Simples Nacional = 0 (está no DAS).</div>
        <button onClick={() => salvar.mutate()} className="btn-primary text-sm self-end">Salvar</button>
      </div>
    </div>
  )
}

function Folha() {
  const qc = useQueryClient()
  const [comp, setComp] = useState(() => new Date().toISOString().slice(0, 7))
  const [editItem, setEditItem] = useState<any>(null)
  const [cfgOpen, setCfgOpen] = useState(false)
  const [pagarOpen, setPagarOpen] = useState(false)
  const { data, isLoading } = useQuery<any>({ queryKey: ['folha', comp], queryFn: async () => (await api.get('/rh/folha', { params: { competencia: comp } })).data })
  async function exportar() {
    try {
      const r = await api.get(`/rh/folha/${data.id}/export`, { responseType: 'blob' })
      const url = URL.createObjectURL(r.data); const a = document.createElement('a')
      a.href = url; a.download = `folha_${comp}.csv`; a.click(); URL.revokeObjectURL(url)
    } catch { toast.error('Erro ao exportar') }
  }
  const refresh = () => qc.invalidateQueries({ queryKey: ['folha', comp] })
  const gerar = useMutation({ mutationFn: async () => api.post('/rh/folha/gerar', { competencia: comp }),
    onSuccess: (r: any) => { toast.success(`Folha gerada — ${r.data.colaboradores} colaborador(es)`); refresh() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro') })
  const fechar = useMutation({ mutationFn: async () => api.post(`/rh/folha/${data.id}/fechar`),
    onSuccess: (r: any) => { toast.success(`Folha fechada — ${r.data.contas_geradas} conta(s) a pagar geradas no Financeiro`, { duration: 6000 }); refresh() } })
  const reabrir = useMutation({ mutationFn: async () => api.post(`/rh/folha/${data.id}/reabrir`),
    onSuccess: (r: any) => { toast.success(`Folha reaberta — ${r.data.contas_estornadas} conta(s) estornadas`); refresh() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro') })
  const fechada = data?.status === 'fechada'
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input type="month" className="input !py-1.5 text-sm w-44" value={comp} onChange={e => setComp(e.target.value)} />
        <button onClick={() => gerar.mutate()} disabled={gerar.isPending || fechada} className="btn-primary text-sm">{data?.existe ? 'Regerar' : 'Gerar folha'}</button>
        <button onClick={() => setCfgOpen(true)} className="btn-ghost text-sm flex items-center gap-1"><Briefcase size={14} /> Encargos</button>
        {data?.existe && <button onClick={exportar} className="btn-ghost text-sm flex items-center gap-1" title="CSV para o contador (base p/ Fortes)">📄 Exportar</button>}
        <div className="flex-1" />
        {data?.existe && fechada && <span className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-700 font-medium">🔒 Fechada{data.provisionado ? ` · ${data.contas_geradas} no Financeiro` : ''}</span>}
        {data?.existe && fechada && <button onClick={() => setPagarOpen(true)} className="btn-primary text-sm">💸 Pagar folha</button>}
        {data?.existe && (fechada
          ? <button onClick={() => reabrir.mutate()} className="btn-ghost text-sm text-amber-700">Reabrir</button>
          : <button onClick={() => fechar.mutate()} className="btn-secondary text-sm">Fechar + provisionar</button>)}
      </div>
      {isLoading && <div className="p-8 text-center text-ink-400"><Loader2 className="animate-spin inline" /></div>}
      {data && !data.existe && <div className="card card-body text-center text-ink-400 py-10">Sem folha em {comp}. Clique em <b>Gerar folha</b>.</div>}
      {data?.existe && (<>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-[11px] text-amber-800">⚠️ Folha <b>gerencial</b> (provisão/fluxo de caixa) — INSS/IRRF aproximados. Não substitui a folha legal/eSocial.</div>
        <div className="grid grid-cols-3 gap-3">
          <div className="card card-body !py-2 text-center"><div className="text-[11px] text-ink-500">Líquido (a pagar)</div><div className="text-xl font-bold">{formatMoney(data.total_liquido)}</div></div>
          <div className="card card-body !py-2 text-center"><div className="text-[11px] text-ink-500">Encargos</div><div className="text-xl font-bold text-amber-600">{formatMoney(data.total_encargos)}</div></div>
          <div className="card card-body !py-2 text-center"><div className="text-[11px] text-ink-500">Custo total (empresa)</div><div className="text-xl font-bold text-rose-600">{formatMoney(data.total_custo)}</div></div>
        </div>
        <div className="table-wrap"><table className="w-full text-sm">
          <thead><tr className="text-left text-ink-500 border-b text-xs">
            <th className="py-2 px-2">Colaborador</th><th className="text-right">Base</th><th className="text-right">Comissão</th>
            <th className="text-right">Prov.</th><th className="text-right">Desc.</th><th className="text-right">INSS</th><th className="text-right">IRRF</th>
            <th className="text-right">Líquido</th><th className="text-right">Custo</th><th></th></tr></thead>
          <tbody>
            {data.itens.map((it: any) => {
              const sumP = (it.proventos || []).reduce((s: number, p: any) => s + Number(p.valor || 0), 0)
              const sumD = (it.descontos || []).reduce((s: number, d: any) => s + Number(d.valor || 0), 0)
              return (
                <tr key={it.id} className="border-b hover:bg-gray-50">
                  <td className="py-1.5 px-2">{it.colaborador_nome}{it.saldo_banco_min ? <span className={`ml-1 text-[10px] ${it.saldo_banco_min < 0 ? 'text-rose-500' : 'text-emerald-600'}`}>banco {Math.round(it.saldo_banco_min / 60 * 10) / 10}h</span> : null}</td>
                  <td className="text-right">{formatMoney(it.salario_base)}</td>
                  <td className="text-right text-emerald-700">{it.comissao ? formatMoney(it.comissao) : '—'}</td>
                  <td className="text-right text-emerald-700">{sumP ? formatMoney(sumP) : '—'}</td>
                  <td className="text-right text-rose-600">{sumD ? formatMoney(sumD) : '—'}</td>
                  <td className="text-right text-ink-500">{formatMoney(it.inss)}</td>
                  <td className="text-right text-ink-500">{formatMoney(it.irrf)}</td>
                  <td className="text-right font-semibold">{formatMoney(it.liquido)}</td>
                  <td className="text-right font-semibold text-rose-600">{formatMoney(it.custo_total)}</td>
                  <td>{!fechada && <button onClick={() => setEditItem(it)} className="text-ink-400 hover:text-brand-600"><Pencil size={14} /></button>}</td>
                </tr>
              )
            })}
          </tbody>
        </table></div>
      </>)}
      {editItem && <FolhaItemModal item={editItem} onClose={() => setEditItem(null)} onSaved={refresh} />}
      {cfgOpen && <ConfigFolhaModal onClose={() => setCfgOpen(false)} />}
      {pagarOpen && <PagarFolhaModal folhaId={data.id} liquido={data.total_liquido} onClose={() => setPagarOpen(false)} onPaid={refresh} />}
    </div>
  )
}

function PagarFolhaModal({ folhaId, liquido, onClose, onPaid }: { folhaId: number; liquido: number; onClose: () => void; onPaid: () => void }) {
  const [bancoId, setBancoId] = useState<number | ''>('')
  const [incluirEncargos, setIncluirEncargos] = useState(false)
  const [dataPg, setDataPg] = useState(() => new Date().toISOString().slice(0, 10))
  const { data: bancos = [] } = useQuery<any[]>({ queryKey: ['contas-banco-rh'], queryFn: async () => (await api.get('/financeiro/contas-bancarias')).data })
  const pagar = useMutation({
    mutationFn: async () => api.post(`/rh/folha/${folhaId}/pagar`, { conta_bancaria_id: bancoId, data_pagamento: dataPg, incluir_encargos: incluirEncargos }),
    onSuccess: (r: any) => { toast.success(`${r.data.contas_pagas} conta(s) pagas — ${formatMoney(r.data.total)} saiu do banco`, { duration: 6000 }); onPaid(); onClose() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h3 className="font-bold">💸 Pagar folha</h3><button onClick={onClose}><X size={18} className="text-ink-400" /></button></div>
        <p className="text-xs text-ink-500">Baixa os salários (líquido {formatMoney(liquido)}) e <b>debita o banco escolhido</b>. Encargos (FGTS/INSS/IRRF) têm vencimento próprio.</p>
        <div><label className="text-xs text-ink-500">Banco / conta de onde sai</label>
          <select className="input !py-1.5 text-sm" value={bancoId} onChange={e => setBancoId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">— escolha —</option>{bancos.map((b: any) => <option key={b.id} value={b.id}>{b.nome}</option>)}</select></div>
        <div><label className="text-xs text-ink-500">Data do pagamento</label><input type="date" className="input !py-1.5 text-sm" value={dataPg} onChange={e => setDataPg(e.target.value)} /></div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={incluirEncargos} onChange={e => setIncluirEncargos(e.target.checked)} /> Pagar também FGTS/INSS/IRRF agora</label>
        <div className="flex justify-end gap-2"><button onClick={onClose} className="px-3 py-2 text-sm rounded border">Cancelar</button>
          <button onClick={() => pagar.mutate()} disabled={!bancoId || pagar.isPending} className="px-4 py-2 text-sm rounded bg-brand-600 text-white disabled:opacity-50">Pagar</button></div>
      </div>
    </div>
  )
}

// ==================================================== PÁGINA
export default function RHPage() {
  const [tab, setTab] = useState<'dashboard' | 'colaboradores' | 'cargos' | 'jornadas' | 'folha'>('dashboard')
  const tabs = [['dashboard', 'Painel'], ['colaboradores', 'Colaboradores'], ['cargos', 'Cargos & Departamentos'], ['jornadas', 'Jornadas & Ponto'], ['folha', 'Folha']] as const
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Users className="text-brand-600" />
        <h1 className="text-2xl font-bold">RH · Setor Pessoal</h1>
      </div>
      <div className="flex gap-1 border-b">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k as any)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? 'border-brand-600 text-brand-600' : 'border-transparent text-ink-500'}`}>{l}</button>
        ))}
      </div>
      {tab === 'dashboard' && <DashboardRH />}
      {tab === 'colaboradores' && <Colaboradores />}
      {tab === 'cargos' && <CargosDeptos />}
      {tab === 'jornadas' && <JornadasPonto />}
      {tab === 'folha' && <Folha />}
    </div>
  )
}
